/**
 * Vertrag zwischen SPA und API von pat-app (Antwortformen der HTTP-Schnittstelle).
 *
 * Entitäten und Schlüssel kommen aus `@pat/core`; hier liegen nur die DTOs, die die
 * API nach außen gibt. Interne Felder (z. B. `ebay.secretRef`, `legacyU`) erscheinen
 * bewusst nicht. Die API validiert jede Antwort gegen diese Schemas (Vertragstests),
 * die SPA parst damit jede Antwort.
 */
import { z } from 'zod';
import { AccountStatus, EbaySiteId, EmbedState, IsoDateTime, JobType, MemberRole, MigrationState, TenantFeatures, TenantId, DescriptionOrderOutcome } from '@pat/core/entities';

export const ApiError = z.object({
  error: z.object({
    /** `quota` (429): Tageskontingent erschöpft, z. B. Uploads je Mandant (Stufe G.2). */
    code: z.enum(['unauthorized', 'forbidden', 'tenant-unavailable', 'not-found', 'conflict', 'invalid-request', 'unavailable', 'busy', 'quota', 'internal']),
    message: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;
export type ApiErrorCode = ApiError['error']['code'];

// ---------------------------------------------------------------------------
// GET /me
// ---------------------------------------------------------------------------

export const Capabilities = z.object({
  login: z.boolean(),
  view: z.boolean(),
  editors: z.boolean(),
  connectEbay: z.boolean(),
  jobs: z.boolean(),
});
export type Capabilities = z.infer<typeof Capabilities>;

export const MembershipView = z.object({
  tenantId: TenantId,
  displayName: z.string(),
  /** eBay-Nutzername aus der Verbindung, für die Kontoauswahl (wie im Altsystem); fehlt = nicht verbunden. */
  ebayUserName: z.string().optional(),
  /** Kündigungsstichtag (exklusiv) bei `cancel-scheduled`, für „Ihre Konten“. */
  cancelEffectiveAt: IsoDateTime.optional(),
  role: MemberRole,
  features: TenantFeatures,
  migrationState: MigrationState,
  accountStatus: AccountStatus,
  /** Abgeleitet aus Umzugs- und Kontostatus; `view: false` = Konto in der App nicht nutzbar. */
  capabilities: Capabilities,
  /**
   * Gesperrtes oder geschlossenes Konto, Inhaber (Entscheidung 25.10.2026): nur `GET …/invoices` und
   * `GET …/invoices/{no}/url`, alles andere bleibt gesperrt; `capabilities.view` ist `false`.
   */
  invoicesOnly: z.literal(true).optional(),
  /**
   * App-BETA (`apps/app/docs/BETA_PLAN.md` §4): Beschreibungen erstellen und als HTML kopieren; PAT schreibt nichts zu eBay,
   * Galerien nur lesen. Die API sperrt die betroffenen Routen selbst (403).
   */
  beta: z.literal(true).optional(),
});
export type MembershipView = z.infer<typeof MembershipView>;

export const MeResponse = z.object({
  userId: z.string(),
  memberships: z.array(MembershipView),
  /** Kontaktadresse für Fragen (BETA-Hinweis, Claude 30.10.2026), aus der Stage-Konfiguration; fehlt = ohne Adresse. */
  supportEmail: z.string().email().max(254).optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ---------------------------------------------------------------------------
// GET /tenants/{t}/status
// ---------------------------------------------------------------------------

/** Restlaufzeit, ab der die Startseite an das Neuverbinden erinnert (APP_KONZEPT.md). */
export const TOKEN_WARN_DAYS = 30;

export const EbayConnectionView = z.object({
  /** `invalid`: Worker hat das Konto wegen ungültigem Token pausiert, auch vor Ablauf. */
  state: z.enum(['ok', 'expiring', 'expired', 'invalid', 'missing']),
  userName: z.string().optional(),
  expiresAt: IsoDateTime.optional(),
  /** Ganze Tage bis zum Ablauf, negativ nach Ablauf. */
  daysLeft: z.number().int().optional(),
  /** Ablaufdatum stammt aus dem Altsystem (Schätzung), nicht von eBay bestätigt. */
  expiresAtEstimated: z.boolean().optional(),
  primarySiteId: EbaySiteId.optional(),
});
export type EbayConnectionView = z.infer<typeof EbayConnectionView>;

export const JobView = z.object({
  type: JobType,
  state: z.enum(['idle', 'running', 'failed', 'paused']),
  lastRunAt: IsoDateTime.optional(),
  lastSuccessAt: IsoDateTime.optional(),
  nextRunAt: IsoDateTime.optional(),
  lastError: z.object({ at: IsoDateTime, code: z.string(), message: z.string() }).optional(),
  /** Zähler des letzten Laufs (z. B. `description.reconcile`: scanned, queued, blocked); fehlt ohne Zähler. */
  counters: z.record(z.string(), z.number().int().nonnegative()).optional(),
});
export type JobView = z.infer<typeof JobView>;
export const StoreSyncRequestView = z.object({ available: z.boolean(), pending: z.boolean(), requestedAt: IsoDateTime.optional() });
export type StoreSyncRequestView = z.infer<typeof StoreSyncRequestView>;
export const StoreSyncRefreshRequest = z.object({}).strict();

/**
 * Live-Update (Flori 25.10.2026): `allowed` = Tarif-Recht (`features.liveUpdate`), `enabled` = Opt-in des Kunden
 * (`Tenant.liveUpdateOptIn`); nur beides zusammen lässt PAT in Beschreibungen schreiben. `changedAt` ist zugleich die
 * Kennung für den Wechsel (Konflikt bei veraltetem Stand).
 */
export const LiveUpdateView = z.object({
  allowed: z.boolean(),
  enabled: z.boolean(),
  changedAt: IsoDateTime.optional(),
  intervalDays: z.number().int().min(1).max(365),
  /** App-BETA (Flori 31.10.2026): Umschalten schreibt synchron auch ins bisherige PAT, das bis zum Umzug das Live-Update ausführt. */
  writeThrough: z.literal(true).optional(),
  /** Angebote ins Live-Update des bisherigen PAT stellen (Flori 02.11.2026); nur mit Write-through. */
  singleItem: z.literal(true).optional(),
});
export type LiveUpdateView = z.infer<typeof LiveUpdateView>;
/** Ein-/Ausschalten; `expectedChangedAt` = gezeigter Stand (`null`, wenn noch nie geschaltet). */
export const LiveUpdateOptInRequest = z.object({ enabled: z.boolean(), expectedChangedAt: IsoDateTime.nullable() }).strict();
export const LiveUpdateOptInResponse = z.object({ liveUpdate: LiveUpdateView });
export type LiveUpdateOptInResponse = z.infer<typeof LiveUpdateOptInResponse>;
/**
 * „eBay-Angebote updaten“ (Flori 02.11.2026, wie LC Cross-Selling → Live-Update „Einzelnes ebay Angebot updaten“, hier
 * mehrere auf einmal): stellt die Angebote im bisherigen PAT in die Live-Update-Warteschlange; dessen Cron revidiert sie beim
 * nächsten Lauf. Ergebnis je Angebot; `taskTriggered` = Lauf sofort angestoßen.
 */
export const EbayItemNumber = z.string().regex(/^[1-9][0-9]{8,18}$/);
export const MAX_LIVE_UPDATE_ITEMS = 50;
export const LiveUpdateItemsRequest = z.object({
  ebayItemIds: z.array(EbayItemNumber).min(1).max(MAX_LIVE_UPDATE_ITEMS).refine(ids => new Set(ids).size === ids.length),
}).strict();
export const LiveUpdateItemOutcome = z.enum(['queued', 'not-found', 'item-ended', 'ambiguous']);
export type LiveUpdateItemOutcome = z.infer<typeof LiveUpdateItemOutcome>;
export const LiveUpdateItemsResponse = z.object({
  results: z.array(z.object({ ebayItemId: EbayItemNumber, outcome: LiveUpdateItemOutcome })),
  taskTriggered: z.boolean(),
});
export type LiveUpdateItemsResponse = z.infer<typeof LiveUpdateItemsResponse>;

export const StatusResponse = z.object({
  tenantId: TenantId,
  displayName: z.string(),
  migrationState: MigrationState,
  accountStatus: AccountStatus,
  cancelEffectiveAt: IsoDateTime.optional(),
  capabilities: Capabilities,
  liveUpdate: LiveUpdateView,
  ebay: EbayConnectionView,
  sync: z.object({
    lastReconcileAt: IsoDateTime.optional(),
    lastReconcileComplete: z.boolean(),
    /** Letzter erfolgreicher Delta-Abgleich laut `JOB#sync.delta`. */
    lastDeltaAt: IsoDateTime.optional(),
    pauseReason: z.enum(['token-invalid', 'rate-limit', 'manual']).optional(),
    /** Seit wann pausiert; bei `manual` auch die Kennung für „Pause aufheben“ (nur dieser Stand wird aufgehoben). */
    pausedAt: IsoDateTime.optional(),
    /** `rate-limit`: ab dann setzt der Worker automatisch fort. */
    resumeAfter: IsoDateTime.optional(),
    /** Nach „neu verbinden“ steht die Wiederaufnahme des Abgleichs noch aus (Rückweg `POST …/ebay/resume`). */
    resumePending: z.literal(true).optional(),
  }),
  jobs: z.array(JobView),
  storeSyncRequest: StoreSyncRequestView.optional(),
  /** Aus `TENANT#t/STATS`; `null` = vom Worker (noch) nicht geliefert, nie geschätzt. */
  counts: z.object({
    activeItems: z.number().int().nonnegative().nullable(),
    itemsWithReviseError: z.number().int().nonnegative().nullable(),
    pendingRevisions: z.number().int().nonnegative().nullable(),
    computedAt: IsoDateTime.optional(),
  }),
  /**
   * Shopkategorien des Kontos (`STORECATS`): bei BETA-Konten aus dem Legacy-Import, sonst aus `store.sync`. Fehlt = noch
   * nichts übernommen oder gerade nicht lesbar (die Übersicht bleibt trotzdem nutzbar).
   */
  store: z
    .object({
      subscription: z.enum(['subscribed', 'none', 'unknown']),
      categoryCount: z.number().int().nonnegative(),
      fetchedAt: IsoDateTime,
      lastErrorCode: z.string().max(16).optional(),
    })
    .optional(),
  generatedAt: IsoDateTime,
});
export type StatusResponse = z.infer<typeof StatusResponse>;
export type StoreSummaryView = NonNullable<StatusResponse['store']>;

// ---------------------------------------------------------------------------
// GET /tenants/{t}/items
// ---------------------------------------------------------------------------

export const ITEMS_PAGE_DEFAULT = 50;
export const ITEMS_PAGE_MAX = 100;

const booleanParam = z.enum(['true', 'false']).transform(v => v === 'true');

/** Query-Parameter der Angebotsliste; Strings wie in der URL. */
export const ItemsQuery = z
  .object({
    status: z.enum(['active', 'ended', 'all']).default('active'),
    site: z.coerce.number().pipe(EbaySiteId).optional(),
    q: z.string().trim().min(1).max(100).optional(),
    listingType: z.enum(['auction', 'fixed', 'store', 'other']).optional(),
    assigned: booleanParam.optional(),
    categoryId: z.string().regex(/^[0-9]{1,20}$/).optional(),
    storeCategoryId: z.string().regex(/^[0-9]{1,20}$/).optional(),
    limit: z.coerce.number().int().min(1).max(ITEMS_PAGE_MAX).default(ITEMS_PAGE_DEFAULT),
    cursor: z.string().min(1).max(2048).optional(),
  })
  .strict();
export type ItemsQuery = z.infer<typeof ItemsQuery>;

export const ItemView = z.object({
  ebayItemId: z.string(),
  site: EbaySiteId,
  title: z.string(),
  listingType: z.enum(['auction', 'fixed', 'store', 'other']),
  price: z.object({ amount: z.string(), currency: z.string() }),
  pictureUrl: z.string().optional(),
  startTime: IsoDateTime,
  endTime: IsoDateTime,
  ended: z.boolean(),
  categoryId: z.string().optional(),
  storeCategoryId: z.string().optional(),
  sku: z.string().optional(),
  /** Legacy `item_pat_search` (core `Item.patSearch`); nur lesend, nur wenn gesetzt. */
  patSearch: z.string().optional(),
  quantity: z.number().int().optional(),
  quantitySold: z.number().int().optional(),
  /** Wie im Altsystem: geplant, aktiv, ausverkauft, beendet (core `itemStatus`). */
  status: z.enum(['scheduled', 'active', 'soldout', 'ended']),
  hasVariations: z.boolean(),
  articleId: z.string().optional(),
  lastSeenAt: IsoDateTime,
  /** Link zum Angebot auf der eBay-Site; fehlt bei unbekannter Site. */
  ebayUrl: z.string().url().optional(),
  /** Zustand der Beschreibung (`EMBED#`); fehlt, wenn PAT die Beschreibung nicht kennt. */
  embedState: EmbedState.optional(),
  /** Modus aus `EMBED#`: `full` = ganze Beschreibung aus Artikel, `block` = nur Galerieblöcke; fehlt = unbekannt. */
  embedMode: z.enum(['full', 'block']).optional(),
  /** Nicht fatale Renderhinweise der letzten Revision (`Embed.warnings`, Codes mit Bezug), z. B. leere Galeriebox. */
  embedWarnings: z.array(z.string().max(64)).max(20).optional(),
  /** Seit wann die Beschreibung bei eBay fremd geändert ist; nur bei `embedState: 'diverged'`, Bezug für „trotzdem überschreiben“. */
  divergedAt: IsoDateTime.optional(),
  /** Code des letzten Schreib-/Rendererfehlers der Beschreibung (`Embed.lastError.code`, ohne Meldung). */
  embedErrorCode: z.string().max(64).optional(),
  /**
   * Letzter Ausgang eines Auftrags je Angebot (core `DescriptionOrderOutcome`): `written` = ausgeführt, `already-current` = war
   * bereits die beauftragte Fassung (kein Schreiben), `article-changed` = inzwischen andere Fassung veröffentlicht,
   * `source-missing` = Angebot nicht (mehr) zugeordnet; die letzten beiden heißen neu beauftragen.
   */
  orderOutcome: z
    .object({
      outcome: DescriptionOrderOutcome.shape.outcome,
      articleVersion: z.number().int().nonnegative(),
      at: IsoDateTime,
      /** Nur bei 'written' nach ACK-Verlust: Zeitpunkt der Nachprüfung bei eBay (core `inspection.observedAt`); `at` ist dann der Abschluss. */
      inspectedAt: IsoDateTime.optional(),
    })
    .optional(),
  /**
   * Offener Auftrag je Angebot (Outbox `customer-ordered` der letzten 24 Stunden ohne späteren Ausgang; nur Artikelseite):
   * `running` = angenommen bzw. an den Auftrag übergeben, `failed` = Zustellung aufgegeben (neu beauftragen).
   */
  openOrder: z.object({ articleVersion: z.number().int().nonnegative(), orderedAt: IsoDateTime, state: z.enum(['running', 'failed']) }).optional(),
  /**
   * Ungeklärter Schreibausgang (OpenWrite-Locator des Workers): ein Schreibvorgang bei eBay wurde zugelassen, sein Ergebnis
   * ist unbekannt. Solange er besteht, sind neue Schreibaufträge für das Angebot gesperrt; `since` = Zulassung.
   */
  writeUnresolved: z.object({ since: IsoDateTime }).optional(),
});
export type ItemView = z.infer<typeof ItemView>;

export const ItemsResponse = z.object({
  items: z.array(ItemView),
  /** Opaker Fortsetzungswert; fehlt auf der letzten Seite. */
  nextCursor: z.string().optional(),
  /** Sortierung der gelieferten Seite; abhängig vom Zugriffspfad. */
  order: z.enum(['endTime', 'itemId']),
  /** Rolle und Kontozustand erlauben Aufträge je Angebot (wie das Bearbeiten von Artikeln); in der BETA und eingefroren nie. */
  editable: z.boolean(),
});
export type ItemsResponse = z.infer<typeof ItemsResponse>;

export const ItemResponse = z.object({ item: ItemView });
export type ItemResponse = z.infer<typeof ItemResponse>;

/** Höchstens so viele Sicherungen je Angebot zeigt die App (neueste zuerst); `total` nennt alle. */
export const ITEM_BACKUPS_SHOWN = 100;
/**
 * Sicherung der vorherigen eBay-Beschreibung (core `DescriptionBackup`, Flori 25.10.2026), nur Metadaten: Zeitpunkt,
 * Größe, Anlass und Modus des folgenden Schreibvorgangs. Objektschlüssel, Hashes und Bezüge bleiben serverseitig;
 * Zurückspielen folgt später (Phase 4).
 */
export const DescriptionBackupView = z.object({
  capturedAt: IsoDateTime,
  bytes: z.number().int().nonnegative(),
  reason: z.string().max(32),
  mode: z.enum(['full', 'block', 'insert']),
});
export type DescriptionBackupView = z.infer<typeof DescriptionBackupView>;
/**
 * Vorschau je Angebot (Entscheidung 11): gespeicherte Sollfassung aus veröffentlichtem Artikel und Design, wie der Worker
 * sie schreiben würde; **nicht** die aktuelle Beschreibung bei eBay. `observedAt` = Lesezeitpunkt der gespeicherten Daten.
 */
export const ItemPreviewResponse = z.object({
  html: z.string().max(2_000_000),
  rendererVersion: z.string().max(64),
  warnings: z.array(z.string().max(64)).max(50),
  observedAt: IsoDateTime,
  articleVersion: z.number().int().nonnegative(),
  designVersion: z.number().int().nonnegative(),
});
export type ItemPreviewResponse = z.infer<typeof ItemPreviewResponse>;

/**
 * Live-Vorschau je Angebot im Blockmodus (`POST /tenants/:t/items/:id/live-preview`, nur dev/Sandbox): die aktuelle
 * Beschreibung bei eBay mit den Galerieblöcken, wie PAT sie austauschen würde. `observedAt` = Lesezeitpunkt der PAT-Daten;
 * `unchanged` = ein Live-Update würde an der Beschreibung nichts ändern; `blockCount` = gefundene PAT-Galerieblöcke
 * (Vorkommen, 0 = kein Marker, dann ist `unchanged` keine Aussage über aktuelle Blöcke). Bei eBay wird nichts geändert.
 */
export const LiveBlockPreviewResponse = z.object({
  html: z.string().max(2_000_000),
  rendererVersion: z.string().max(64),
  warnings: z.array(z.string().max(64)).max(50),
  observedAt: IsoDateTime,
  unchanged: z.boolean(),
  blockCount: z.number().int().min(0).max(50),
});
export type LiveBlockPreviewResponse = z.infer<typeof LiveBlockPreviewResponse>;
export const ItemBackupsResponse = z.object({ backups: z.array(DescriptionBackupView).max(ITEM_BACKUPS_SHOWN), total: z.number().int().nonnegative() });
export type ItemBackupsResponse = z.infer<typeof ItemBackupsResponse>;

/**
 * „Diese Beschreibung trotzdem überschreiben“: `divergedAt` wie angezeigt, damit nur der Stand
 * überschrieben wird, den der Kunde bestätigt hat (sonst 409, neu laden).
 */
export const ItemOverwriteRequest = z.object({ divergedAt: IsoDateTime }).strict();
export type ItemOverwriteRequest = z.infer<typeof ItemOverwriteRequest>;

/** Auftrag vorgemerkt (Outbox); ausgeführt wird er vom Worker, sobald er den Abgleich annimmt. */
export const ItemOverwriteResponse = z.object({ requested: z.literal(true) });
export type ItemOverwriteResponse = z.infer<typeof ItemOverwriteResponse>;

// ---------------------------------------------------------------------------
// eBay neu verbinden (Auth'n'Auth, Stufe C)
// ---------------------------------------------------------------------------

/** Erlaubte Ziele der Sign-in-Weiterleitung; die SPA prüft die URL vor dem Wechsel. */
/** `signin.ebay.de`: Production-Anmeldung der App-BETA wie im bisherigen PAT (`packages/ebay` `ProductionConsentClient`). */
export const EBAY_SIGNIN_HOSTS = ['signin.ebay.com', 'signin.ebay.de', 'signin.sandbox.ebay.com'] as const;

export const EbayConnectResponse = z.object({
  signInUrl: z.string().url(),
  /** Ablauf der SessionID (5 Minuten). */
  expiresAt: IsoDateTime,
});
export type EbayConnectResponse = z.infer<typeof EbayConnectResponse>;

export const EbayCompleteRequest = z
  .object({
    /** Bestätigt genau diesen (angezeigten) neuen eBay-Nutzer für das Konto. */
    confirmNewUserName: z.string().trim().min(1).max(64).optional(),
  })
  .strict();
export type EbayCompleteRequest = z.infer<typeof EbayCompleteRequest>;

export const EbayCompleteResponse = z.discriminatedUnion('result', [
  z.object({
    result: z.literal('connected'),
    ebay: EbayConnectionView,
    userChanged: z.boolean(),
    /** `false`: Abgleich nicht aufgenommen (Umzugszustand ohne Jobs, Pause oder offene Wiederaufnahme). */
    syncQueued: z.boolean(),
    /** Verbindung gespeichert, Wiederaufnahme des Abgleichs offen; Rückweg `POST …/ebay/resume`. */
    syncPending: z.literal(true).optional(),
    /** App-BETA (Flori 31.10.2026): das neue Token gilt auch im bisherigen PAT (Write-through vor PAT 4). */
    legacyUpdated: z.literal(true).optional(),
    /** App-BETA (Flori 01.11.2026): dasselbe eBay-Konto unter neuem Namen übernommen, Angebote bleiben. */
    renamed: z.literal(true).optional(),
    /** App-BETA (Flori 01.11.2026): auf ein anderes eBay-Konto gewechselt; Angebote werden neu eingelesen. */
    accountSwitched: z.literal(true).optional(),
  }),
  z.object({
    result: z.literal('confirm-user-change'),
    previousUserName: z.string(),
    newUserName: z.string(),
    /** App-BETA: kein Kontowechsel, sondern ein neuer Name desselben (oder noch nicht eindeutig bekannten) eBay-Kontos. */
    rename: z.literal(true).optional(),
    /** App-BETA: Wechsel auf ein anderes eBay-Konto; bisherige Angebote und Shopkategorien werden entfernt und neu eingelesen. */
    accountSwitch: z.literal(true).optional(),
  }),
]);
export type EbayCompleteResponse = z.infer<typeof EbayCompleteResponse>;

export const EbayResumeResponse = z.object({ resumed: z.boolean() });
/** „Pause aufheben“ (nur manuelle Pause): `pausedAt` wie angezeigt, damit keine neuere Pause aufgehoben wird. */
export const PauseLiftRequest = z.object({ pausedAt: IsoDateTime }).strict();
export const PauseLiftResponse = z.object({ lifted: z.literal(true) });
export type PauseLiftResponse = z.infer<typeof PauseLiftResponse>;
export type EbayResumeResponse = z.infer<typeof EbayResumeResponse>;
